import { buildContactEndAdornment } from 'components/party/buildContactEndAdornment';
import React, { ReactNode } from 'react';
import { PartyNameParty, renderPartyNumberAndIcon } from 'utils/partyNameUtil';
import {
    DynamicMultiselectFilterForm,
    MainFilterForm,
    MainFilterInForm,
    QuickFilterForm,
    SelectOption,
} from '../../ListTableTypes.tsx';
import { DynamicMultiselect } from './DynamicMultiSelect.tsx';

/**
 * Contact-aware wrapper around the table filter-piece `DynamicMultiselect`. Same principle as the
 * `ContactDynamic*` form-input wrappers: the base filter-piece stays option-type agnostic, and this
 * wrapper pre-wires the adornments every contact filter should share — party-type icon +
 * `[partyNumber]` at the start, and `(Deceased)` / `(Deleted)` text suffixes plus the Persona
 * non grata chip at the end (combined with any caller-supplied `getOptionEndAdornment`).
 *
 * Used automatically by `MainFilters` when the filter config has `subType: 'contact'`; the new design's
 * + Filter checklist takes the same adornments through {@link withContactAdornments}.
 */
interface Props {
    filter: DynamicMultiselectFilterForm;
    quickFilters: QuickFilterForm;
    mainFilters: MainFilterForm;
    mainFiltersInForm: MainFilterInForm[];
    onChange: (value: SelectOption[], groupSelector: string) => void;
    onExcludingChange?: (isExcluding: boolean, groupSelector: string) => void;
}

/** `filter` with the adornments every contact filter shows, in the classic multi-select and + Filter alike. */
export const withContactAdornments = (filter: DynamicMultiselectFilterForm): DynamicMultiselectFilterForm => {
    const callerEndAdornment = filter.getOptionEndAdornment;
    return {
        ...filter,
        getOptionStartAdornment: renderPartyNumberAndIcon,
        getOptionEndAdornment: (option: unknown): ReactNode => {
            const caller =
                typeof callerEndAdornment === 'function'
                    ? (callerEndAdornment as (o: unknown) => ReactNode)(option)
                    : null;
            return buildContactEndAdornment(option as PartyNameParty, caller);
        },
    };
};

const ContactDynamicMultiselect: React.FC<Props> = (props) => (
    <DynamicMultiselect {...props} filter={withContactAdornments(props.filter)} />
);

export default ContactDynamicMultiselect;
